/**
 * The slide-in glass panel that hosts the voice copilot UI.
 *
 * Visual structure:
 *   header        — Noki branding + status pill
 *   transcript    — final user/assistant turns
 *   live partial  — streaming TTS / interim STT (only when active)
 *   visualizer    — pulsing bars driven by current speech level
 *   mic button    — connect / disconnect
 *
 * The panel doesn't know about Pipecat — that's `useVoiceClient`. It
 * only renders state and dispatches connect/disconnect.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AudioVisualizer } from "./AudioVisualizer";
import { LivePartialStrip } from "./LivePartialStrip";
import { MicButton } from "./MicButton";
import { PauseResumeButton } from "./PauseResumeButton";
import { StatusIndicator } from "./StatusIndicator";
import { TranscriptPanel } from "./TranscriptPanel";
import { UsageSection } from "./UsageSection";
import { useCopilotUsage } from "./useCopilotUsage";
import { WorkflowCard } from "./WorkflowCard";
import {
  invokeCopilotAction,
  listCopilotActions,
  subscribeCopilotActions,
} from "./actionRegistry";
import { useControlSocket } from "./controlSocket";
import { resultStatus, failedStatus, manualStatus } from "./actionStatus";
import { resolveNodeId, shouldEmitManualNav } from "./routeNodes";
import { formatResetTime } from "./resetTime";
import { useGoalStore } from "./goalStore";
import {
  getMergedPageContext,
  subscribeCopilotPageContext,
} from "./pageContextRegistry";
import { clearCopilotSessionId, ensureCopilotSessionId } from "./sessionId";
import { glassPanelStyle, tokens } from "./styles";
import { useVoiceClient } from "./useVoiceClient";

const TOOL_CALL_FALLBACK_TIMEOUT_MS = 5_000;
const FALLBACK_PAGE_ID = "noki-fe.global";
// Narration-sync: a route change within this window of an agent action.status
// is treated as agent-caused (already reported), not a manual navigation.
// Matches the backend _ROUTE_CHANGE_RECENCY_MS gate.
const AGENT_NAV_SUPPRESS_MS = 2_500;
// Sentinel so the manual-nav effect can seed on first mount without emitting
// (distinct from a resolved node id OR a null/unmapped route).
const NODE_UNSET = Symbol("node-unset");

/**
 * Build the context snapshot pushed over /ws/copilot.
 *
 * Top-level structural fields come from the location bar and the
 * actionRegistry. Per-page contributions (description, capabilities,
 * tool ids, structural salient state) come from the pageContextRegistry
 * — pages opt in via `useCopilotPageContext`. The salient dict is
 * folded into the wire schema's `state` (which is `dict[str, scalar]`
 * server-side); the description / capabilities / tool ids ride as
 * additive top-level optional fields the server learned to surface in
 * `_get_current_context`.
 */
const buildContextSnapshot = () => {
  const merged = getMergedPageContext();
  const route =
    typeof window !== "undefined" && window.location
      ? `${window.location.pathname}${window.location.search}`
      : "/";
  // The set of pageIds whose protocols are active right now. Used by
  // both `listCopilotActions` (filter what we advertise) and
  // `invokeCopilotAction` (pick the scope-correct handler when an
  // action name is registered under multiple scopes).
  const activeProtocolIds = (merged.activeProtocols || []).map((p) => p.pageId);
  return {
    page_id: merged.pageId || FALLBACK_PAGE_ID,
    route,
    description: merged.description || null,
    capabilities: merged.capabilities,
    tool_ids: merged.toolIds,
    available_actions: listCopilotActions(activeProtocolIds).map((a) => a.name),
    state: merged.salient,
    // Stack-shaped metadata for the server's active-protocol filter
    // (Delta E in the plan). Server rejects tool calls whose tool name
    // isn't owned by any of these layers.
    active_protocols: (merged.activeProtocols || []).map((p) => ({
      page_id: p.pageId,
      parent_page_id: p.parentPageId || null,
      level: p.level,
      exclusive: p.exclusive,
    })),
    // Latent sub-views advertised by the active protocols' parents but
    // not yet mounted (closed popovers, collapsed panels, modals not yet
    // opened). The agent narrates these as "you can also … by saying
    // <activation_voice_tool>". See voice_copilot_design.md §7 + the
    // tree-composition R&D doc.
    latent_capabilities: (merged.latentCapabilities || []).map((c) => ({
      page_id: c.pageId,
      parent_page_id: c.parentPageId || null,
      short_description: c.shortDescription || "",
      capabilities: Array.isArray(c.capabilities) ? c.capabilities : [],
      activation_via: c.activationVia || "voice_action",
      activation_voice_tool: c.activationVoiceTool || "",
      level: c.level || "drawer",
    })),
  };
};

const CloseIcon = () => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

// Threshold below which the panel renders the compact button + spacing
// variant. 720px keeps the design-intent layout on 13"+ laptops at full
// browser height, and gracefully shrinks on smaller / split-screen
// configurations where the bottom controls would otherwise scroll off.
const COMPACT_VIEWPORT_HEIGHT = 720;

function useIsCompactViewport() {
  // SSR-safe initial value; on the client the effect below will set
  // the true reading on the first paint. We avoid `useSyncExternalStore`
  // here to keep the dependency surface minimal — this hook is used in
  // exactly one place.
  const [compact, setCompact] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.innerHeight < COMPACT_VIEWPORT_HEIGHT,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      setCompact(window.innerHeight < COMPACT_VIEWPORT_HEIGHT);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return compact;
}

export function NokiCopilotPanel({ open, onClose, serverUrl, onStatusChange }) {
  const isCompact = useIsCompactViewport();
  // sessionId is reactive (not a ref) so the server's session_closed
  // signal can rotate it; the controlSocket effect re-runs with the
  // new id and reconnects cleanly. ensureCopilotSessionId is the
  // lazy-init form — it reads sessionStorage at first render only.
  const [sessionId, setSessionId] = useState(() => ensureCopilotSessionId());
  const handleSessionClosed = useCallback(() => {
    clearCopilotSessionId();
    setSessionId(ensureCopilotSessionId());
  }, []);

  // Governance banner state — the latest server-pushed BudgetNotice (80%
  // heads-up or the final wind-down reason). Rendered in the footer.
  const [budgetNotice, setBudgetNotice] = useState(null);
  // Daily usage snapshot for the in-panel meter. Polls only while the panel is
  // open; refetched on budget notices and when a session ends (below) so the
  // meter reflects freshly-spent minutes/cost without waiting for the poll.
  const { usage: usageSnapshot, refetch: refetchUsage } = useCopilotUsage(
    serverUrl,
    open,
  );
  const handleBudgetNotice = useCallback((notice) => {
    // Two-stage idle: pause/resume are mic-mute toggles, not terminal banners.
    // The muted banner (driven by the mute effect) shows the reason, so we do
    // NOT also raise a budget banner for these.
    if (notice?.kind === "idle_pause") {
      setAutoPaused(true);
      return;
    }
    if (notice?.kind === "idle_resume") {
      setAutoPaused(false);
      return;
    }
    // A terminal governance notice (duration/daily/idle stop) means usage just
    // moved — refresh the meter so it reflects the crossing immediately.
    refetchUsage();
    // The server keeps the control session usable after either kind of
    // wind-down, so we do NOT rotate the session id here:
    //   * session_duration — the session stays live; a fresh mic click starts
    //     a new pipeline (fresh per-session timer) on the same session using
    //     its pong-refreshed pair token. (Rotating here used to race the old
    //     peer's teardown and cause a replayed-token failure on restart.)
    //   * daily_budget — the next offer is refused (402) until the reset; the
    //     banner shows the resume time. No rotation needed.
    // A truly dead session (server error / logout) still rotates via the
    // separate onSessionClosed path.
    setBudgetNotice(notice);
  }, [refetchUsage]);

  const {
    status,
    errorMessage,
    transcript,
    livePartial,
    userLevelRef,
    assistantLevelRef,
    isAssistantSpeaking,
    isMicMuted,
    muteReason,
    setMicMuted,
    connect,
    disconnect,
    // setPairToken is fed below from controlSocket.pairToken so the
    // /api/offer call carries the server-issued HMAC token. See
    // useVoiceClient for the rationale on the indirection.
    setPairToken,
    // Pauses audio-level state updates for ~1.5s after a route
    // change so the new page's lazy chunk gets the main thread to
    // parse + mount. See the route-transition effect below.
    setAudioLevelsPaused,
  } = useVoiceClient(serverUrl, sessionId);

  // Surface session status to the parent so the launcher button can react
  // (e.g. radiate when the panel is closed but the session is still live).
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  // --- Layer-1 clinical-safety mute -----------------------------------------
  // Mute the copilot's microphone ONLY during ACTIVE audio capture for
  // an encounter (recording=true AND paused=false). When the clinician
  // hits Pause, they've explicitly stopped capturing patient audio and
  // typically want to use the copilot in that window — so we unmute
  // during pause. Resume re-engages the encounter capture and the mute
  // returns immediately.
  //
  // Source of truth: walk `state.recognizer.recordingByEncounter` —
  // any entry with `recording === true && paused !== true` triggers
  // the mute. The recognizer reducer treats recording vs paused as
  // mutually exclusive (pause flips recording=false, paused=true), so
  // this check correctly catches active capture only.
  //
  // The legacy `state.recordingStatus.isRecording` and
  // `state.recognizer.isRecordingInProgress` flags fall through ONLY
  // if there are no per-encounter entries — defensive belt for the
  // legacy SpeechToTextComponent paths that may not update the new
  // per-encounter map.
  const isAnyRecordingActive = useSelector((state) => {
    const byEncounter = state?.recognizer?.recordingByEncounter;
    let hasAnyEntry = false;
    if (byEncounter && typeof byEncounter === "object") {
      for (const key of Object.keys(byEncounter)) {
        hasAnyEntry = true;
        const v = byEncounter[key];
        if (v?.recording === true && v?.paused !== true) {
          return true;
        }
      }
    }
    if (hasAnyEntry) {
      // Per-encounter entries exist but none is actively capturing —
      // user is paused or stopping. Unmute the copilot.
      return false;
    }
    // No per-encounter state at all — fall back to legacy globals.
    if (state?.recognizer?.isRecordingInProgress === true) return true;
    if (state?.recordingStatus?.isRecording === true) return true;
    return false;
  });
  // User intent for pause is tracked SEPARATELY from the physical
  // mute state. The physical state (`isMicMuted` in useVoiceClient) is
  // derived from the union of "user wants paused" and "a recording is
  // actively capturing." Splitting the two lets a clinical-safety
  // mute (which clamps mute=true while recording) co-exist with a
  // user-pause intent that survives across recording cycles — without
  // the split, the recording-stop branch would silently clobber the
  // user's pause whenever the recording lock released.
  const [userPaused, setUserPaused] = useState(false);
  // Server-driven auto-pause (two-stage idle): the backend detected silence
  // and sent an `idle_pause` notice. Mutes the mic while keeping the stream
  // warm, so resume is instant. Cleared on `idle_resume`, on manual resume,
  // or when the session ends. Kept distinct from `userPaused` so the mute
  // reason/banner can tell the clinician WHY it paused.
  const [autoPaused, setAutoPaused] = useState(false);
  const isPaused = userPaused || autoPaused;

  // Click lock — kept as state (not a ref) so the button's disabled
  // prop updates in response. 300ms covers a human's accidental
  // double-tap window without feeling laggy on deliberate toggles.
  // The pending-timeout handle lives in a ref so we can clear it on
  // unmount (and on re-entry, in case the user spams) without
  // leaking a callback that runs after the panel is gone.
  const [isPauseLocked, setIsPauseLocked] = useState(false);
  const pauseLockTimerRef = useRef(null);
  useEffect(() => {
    return () => {
      if (pauseLockTimerRef.current !== null) {
        window.clearTimeout(pauseLockTimerRef.current);
        pauseLockTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof setMicMuted !== "function") return;
    // Reason precedence: clinical safety > user intent. If both fire,
    // the banner reads "encounter_recording_active" so the clinician
    // sees the load-bearing reason, not their own pause click.
    const shouldMute = isAnyRecordingActive || userPaused || autoPaused;
    const reason = isAnyRecordingActive
      ? "encounter_recording_active"
      : userPaused
      ? "user_paused"
      : autoPaused
      ? "idle_silence"
      : null;
    setMicMuted(shouldMute, reason);
  }, [isAnyRecordingActive, userPaused, autoPaused, setMicMuted]);

  // Manual pause/resume — toggles the user-intent state only. The
  // combined-policy effect above translates the intent + the
  // clinical-safety lock into the physical mute. Uses functional
  // setState so rapid clicks don't desync via stale closures.
  //
  // Guarded against:
  //   - Click bursts → 300ms post-toggle lock; disables the button
  //     during the window so the user gets visual confirmation.
  //   - Mid-recording clicks → disabled at the button level too; the
  //     handler bails defensively in case the disabled state was
  //     bypassed (keyboard, automation).
  //   - Disconnected sessions → button disabled while not "connected".
  const handleTogglePause = useCallback(() => {
    if (typeof setMicMuted !== "function") return;
    if (isAnyRecordingActive) return; // clinical-safety lock wins
    if (isPauseLocked) return; // debounce
    setIsPauseLocked(true);
    // Resuming clears BOTH the manual pause and any server-driven auto-pause
    // (whichever is active); pausing sets the manual intent.
    if (userPaused || autoPaused) {
      setUserPaused(false);
      setAutoPaused(false);
    } else {
      setUserPaused(true);
    }
    if (pauseLockTimerRef.current !== null) {
      window.clearTimeout(pauseLockTimerRef.current);
    }
    pauseLockTimerRef.current = window.setTimeout(() => {
      pauseLockTimerRef.current = null;
      setIsPauseLocked(false);
    }, 300);
  }, [isAnyRecordingActive, isPauseLocked, setMicMuted, userPaused, autoPaused]);

  // When the session fully ends (status returns to "idle"), clear any pause
  // state. Without this, `userPaused`/`autoPaused` persisted across Stop →
  // Start, so a fresh session opened already muted with a stale banner.
  useEffect(() => {
    if (status === "idle") {
      setUserPaused(false);
      setAutoPaused(false);
      // A session just ended — pull the final tally into the meter.
      refetchUsage();
    }
  }, [status, refetchUsage]);

  const goalStore = useGoalStore();
  const inflightRef = useRef(new Map());
  const sendToolResultRef = useRef(null);
  const sendActionStatusRef = useRef(null);
  // Timestamp (ms) of the last AGENT action.status we emitted. A route change
  // within AGENT_NAV_SUPPRESS_MS of it is treated as agent-caused (already
  // reported), so we don't double-report it as a manual navigation. Mirrors
  // the backend's last_route_change_at recency gate.
  const lastAgentActionAtRef = useRef(0);
  const emitAgentStatus = useCallback((payload) => {
    lastAgentActionAtRef.current = Date.now();
    sendActionStatusRef.current?.(payload);
  }, []);

  // Forward `tool.dispatch` from the server into the action registry.
  const handleToolDispatch = useCallback((dispatch) => {
    const callId = dispatch.call_id;
    const timeoutMs = dispatch.timeout_ms || TOOL_CALL_FALLBACK_TIMEOUT_MS;

    // eslint-disable-next-line no-console
    console.info("[copilot] tool.dispatch.received", {
      callId,
      toolName: dispatch.tool_name,
      args: dispatch.arguments,
      timeoutMs,
    });

    const sendResult = (payload) => {
      // eslint-disable-next-line no-console
      console.info("[copilot] tool.result.sent", {
        callId: payload.callId,
        status: payload.status,
        errorCode: payload.errorCode,
      });
      sendToolResultRef.current?.(payload);
    };

    // Watchdog timer — if the host handler hangs past the negotiated
    // timeout, surface a structured error to the server.
    const timer = window.setTimeout(() => {
      if (!inflightRef.current.has(callId)) return;
      inflightRef.current.delete(callId);
      // eslint-disable-next-line no-console
      console.warn("[copilot] tool.dispatch.host_timeout", {
        callId,
        toolName: dispatch.tool_name,
        timeoutMs,
      });
      sendResult({
        callId,
        status: "error",
        errorCode: "host_timeout",
        errorMessage: "Host timed out responding to the agent",
      });
      emitAgentStatus(failedStatus(dispatch, "host_timeout"));
    }, timeoutMs + 250);
    inflightRef.current.set(callId, timer);

    // Narration-sync: confirm-only, so we DON'T narrate on start. We still
    // stamp the agent-action time here so a route change this dispatch causes
    // is recognised as agent-driven (not double-reported as a manual nav).
    lastAgentActionAtRef.current = Date.now();

    // Pass the currently-active protocol set so scoped actions
    // (e.g. an action registered under "select_patient_drawer" while
    // that drawer is open) take precedence over a same-named global.
    const activeProtocolIds = (
      getMergedPageContext().activeProtocols || []
    ).map((p) => p.pageId);

    invokeCopilotAction(dispatch.tool_name, dispatch.arguments, activeProtocolIds)
      .then((result) => {
        const handle = inflightRef.current.get(callId);
        if (handle !== undefined) {
          window.clearTimeout(handle);
          inflightRef.current.delete(callId);
        }
        sendResult({ callId, ...result });
        // Narration-sync: report the real lifecycle outcome (completed on ok
        // with the resulting node; failed on error). `blocked` is mid-flow and
        // intentionally emits nothing (resultStatus returns null).
        const status = resultStatus(dispatch, result);
        if (status) emitAgentStatus(status);
      })
      .catch((error) => {
        const handle = inflightRef.current.get(callId);
        if (handle !== undefined) {
          window.clearTimeout(handle);
          inflightRef.current.delete(callId);
        }
        // eslint-disable-next-line no-console
        console.error("[copilot] tool.dispatch.bridge_exception", {
          callId,
          toolName: dispatch.tool_name,
          error: error?.message ?? String(error),
        });
        sendResult({
          callId,
          status: "error",
          errorCode: "host_exception",
          errorMessage: error?.message ?? "host handler threw",
        });
        emitAgentStatus(failedStatus(dispatch, "host_exception"));
      });
  }, [emitAgentStatus]);

  const handleGoalEvent = useCallback(
    (event) => goalStore.applyServerEvent(event),
    [goalStore],
  );

  const controlSocket = useControlSocket({
    serverUrl: serverUrl || undefined,
    sessionId,
    enabled: true,
    onToolDispatch: handleToolDispatch,
    onGoalEvent: handleGoalEvent,
    onSessionClosed: handleSessionClosed,
    onBudgetNotice: handleBudgetNotice,
  });

  // A warning-level banner is transient — auto-dismiss it after a few
  // seconds. A final banner persists until a fresh session connects.
  useEffect(() => {
    if (!budgetNotice || budgetNotice.level !== "warning") return undefined;
    const timer = window.setTimeout(() => setBudgetNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [budgetNotice]);

  // Clear any lingering governance banner once a new voice session connects.
  useEffect(() => {
    if (status === "connected") setBudgetNotice(null);
  }, [status]);

  // CRITICAL: destructure into local bindings before using as effect deps.
  // `useControlSocket` returns a fresh object literal every render, so
  // `[controlSocket, ...]` would re-fire every render and re-publish
  // the action registry + context on every paint. That bombards the WS,
  // backs up the JS task queue, and causes inbound `tool.dispatch` frames
  // to wait seconds instead of milliseconds before the message handler
  // runs. The destructured fields are either primitives (`status`) or
  // `useCallback`-stable functions, so deps only change when there's a
  // real reason.
  const {
    status: socketStatus,
    registerActions,
    pushContext,
    sendToolResult,
    sendActionStatus,
    sendCopilotPause,
    pairToken,
  } = controlSocket;

  sendToolResultRef.current = sendToolResult;
  sendActionStatusRef.current = sendActionStatus;

  // Tell the backend whenever the copilot enters/leaves a DELIBERATE
  // non-listening state — the clinician manually paused, or an encounter
  // recording is active. (The server's own idle auto-pause, `autoPaused`, is
  // NOT included: the server drives that itself and must keep counting toward
  // its stop.) This is what stops the idle watchdog from mistaking a paused
  // session for an abandoned one. Re-sent whenever the state OR the socket
  // connection changes, so the server's view re-syncs after a reconnect.
  const externalPause = userPaused || isAnyRecordingActive;
  useEffect(() => {
    if (typeof sendCopilotPause !== "function") return;
    if (socketStatus !== "connected") return;
    sendCopilotPause(externalPause);
  }, [externalPause, socketStatus, sendCopilotPause]);

  // Hand the server-issued pair token down to useVoiceClient so the
  // /api/offer POST carries it. Pair tokens are minted fresh on every
  // WS handshake (and cleared on close), so this effect fires on
  // every reconnect with the new value — useVoiceClient's internal
  // ref always reflects the freshest valid token. A null value
  // (panel just mounted, or WS in flight) makes connect() refuse
  // rather than firing an offer the server would 401.
  useEffect(() => {
    setPairToken(pairToken);
  }, [pairToken, setPairToken]);

  // Route-transition mitigation for the "Loading..." flash on first
  // navigation to a new route in dev. CRA's webpack-dev-server
  // compiles the route's lazy chunk on demand the first time it's
  // requested; while the chunk downloads and the browser parses it,
  // the wrapper's Suspense fallback renders the FallBackLoader (the
  // spinner the user sees in the main content area). The visualizer
  // re-rendering at ~30fps from setUserLevel / setAssistantLevel
  // contends with that parse on the main thread.
  //
  // Pausing the audio-level state updates for 1.5s after every
  // pathname change frees the main thread without affecting the
  // audio engine, mic state, transcript bubbles, or the WS itself —
  // it only stalls the visualizer's bar animation, which goes flat
  // at zero. After the timer expires we resume normal level updates.
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return undefined;
    prevPathRef.current = location.pathname;
    setAudioLevelsPaused(true);
    const handle = window.setTimeout(() => {
      setAudioLevelsPaused(false);
    }, 1500);
    return () => {
      window.clearTimeout(handle);
      // If another route change comes in before our timer fires (or
      // the panel unmounts), force-resume so the visualizer doesn't
      // stay frozen across the transition.
      setAudioLevelsPaused(false);
    };
  }, [location.pathname, setAudioLevelsPaused]);

  // Narration-sync (Phase-2b): report the doctor's OWN navigations to the
  // copilot as manual action.status, so its state stays truthful and it can
  // speak up if a manual move diverges from an in-flight task. A route change
  // within AGENT_NAV_SUPPRESS_MS of an agent action.status was almost
  // certainly caused BY the agent (already reported) — skip it so we never
  // double-report an agent navigation as manual. Mirrors the backend's
  // last_route_change_at recency gate. No emit on first mount.
  const prevNodeRef = useRef(NODE_UNSET);
  useEffect(() => {
    const node = resolveNodeId(location.pathname, location.search);
    if (prevNodeRef.current === NODE_UNSET) {
      prevNodeRef.current = node; // seed on mount; don't emit
      return;
    }
    const prevNode = prevNodeRef.current;
    prevNodeRef.current = node;
    const sinceAgent = Date.now() - lastAgentActionAtRef.current;
    if (shouldEmitManualNav(node, prevNode, sinceAgent, AGENT_NAV_SUPPRESS_MS)) {
      sendActionStatusRef.current?.(manualStatus({ node }));
    }
  }, [location.pathname, location.search]);

  // Re-publish the registered action set + a context snapshot whenever
  // the action registry OR the page-context registry changes, or the
  // WS becomes ready. Two separate revisions are tracked so a change in
  // either triggers a re-push without re-firing the other path.
  // (`actions.register` payload uses FALLBACK_PAGE_ID as a stable scope
  // label — that's the action-set's identity on the server, separate
  // from the per-page semantic id we publish in the context snapshot.)
  const [registryRevision, setRegistryRevision] = useState(0);
  const [pageContextRevision, setPageContextRevision] = useState(0);
  useEffect(() => subscribeCopilotActions(setRegistryRevision), []);
  useEffect(
    () => subscribeCopilotPageContext(setPageContextRevision),
    [],
  );

  useEffect(() => {
    if (socketStatus !== "ready") return;
    const actions = listCopilotActions();
    registerActions({
      pageId: FALLBACK_PAGE_ID,
      version: String(registryRevision),
      tools: actions,
    });
    pushContext(buildContextSnapshot());
  }, [
    socketStatus,
    registerActions,
    pushContext,
    registryRevision,
    pageContextRevision,
  ]);

  // Send context updates on route change (single-page app).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onLocationChange = () => {
      if (socketStatus === "ready") {
        pushContext(buildContextSnapshot());
      }
    };
    window.addEventListener("popstate", onLocationChange);
    return () => window.removeEventListener("popstate", onLocationChange);
  }, [socketStatus, pushContext]);

  // Cancel any inflight tool watchdog timers on unmount.
  useEffect(() => {
    const inflight = inflightRef.current;
    return () => {
      for (const handle of inflight.values()) {
        window.clearTimeout(handle);
      }
      inflight.clear();
    };
  }, []);

  const handleGoalConfirm = useCallback(
    (goalId) => {
      goalStore.markConfirming();
      controlSocket.sendGoalConfirm(goalId);
    },
    [controlSocket, goalStore],
  );

  const handleGoalCancel = useCallback(
    (goalId) => {
      goalStore.markCancelling();
      controlSocket.sendGoalCancel(goalId);
    },
    [controlSocket, goalStore],
  );

  const controlPaneError = controlSocket.lastError;

  const handleToggle = () => {
    if (status === "connected" || status === "error") {
      disconnect();
      return;
    }
    if (status === "idle") {
      connect();
    }
  };

  // Pass the ref (not a value) so the visualizer paints from it via rAF — the
  // ref identity only changes on a speaking-turn flip, never per audio frame.
  const visualizerLevelRef = isAssistantSpeaking ? assistantLevelRef : userLevelRef;
  const visualizerVariant = isAssistantSpeaking ? "assistant" : "user";

  return (
    <motion.section
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={
        open
          ? { opacity: 1, y: 0, scale: 1 }
          : { opacity: 0, y: 24, scale: 0.98 }
      }
      transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
      style={{
        ...glassPanelStyle,
        position: "fixed",
        bottom: 96,
        right: 24,
        // Cap width to the design intent (380px) on roomy displays but
        // shrink with the viewport so the panel never overhangs the
        // right edge on narrow laptops or split-screen layouts. 48px of
        // breathing room = 24px gutter on each side.
        width: "min(380px, calc(100vw - 48px))",
        // Cap height to the design intent (540px) but shrink to fit
        // viewports shorter than ~660px (e.g. 1366x768 with browser
        // chrome, 1280x720 laptops). 132px of vertical breathing room =
        // 96px bottom + 36px top so the header is never clipped under
        // the URL bar.
        height: "min(540px, calc(100vh - 132px))",
        borderRadius: 24,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pointerEvents: open ? "auto" : "none",
        zIndex: 2147483646,
      }}
      role="dialog"
      aria-label="Noki voice copilot"
      aria-hidden={!open}
    >
      <header
        style={{
          padding: isCompact ? "12px 20px 10px" : "18px 20px 14px",
          borderBottom: `1px solid ${tokens.surface.border}`,
          display: "flex",
          flexDirection: "column",
          gap: isCompact ? 8 : 12,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: tokens.ink[300],
              }}
            >
              Noki
            </span>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                color: tokens.ink[50],
                letterSpacing: "-0.005em",
              }}
            >
              Voice copilot
            </h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <StatusIndicator
              status={status}
              isAssistantSpeaking={isAssistantSpeaking}
            />
            <button
              type="button"
              onClick={() => {
                // Hide-only: the X collapses the panel but the WebRTC and
                // control-plane sessions stay alive in the background.
                // The launcher button radiates to signal "session live"
                // and a re-open reuses the same sessionStorage id. Do NOT
                // call sendSessionEnd here — that would flip the server
                // row to status='closed' and the next session.init with
                // the same id would be rejected (session_closed error).
                onClose();
              }}
              aria-label="Close Noki"
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                border: `1px solid ${tokens.surface.border}`,
                background: "rgba(255, 255, 255, 0.04)",
                color: tokens.ink[200],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      </header>

      {/*
        Middle scroll region. The panel is a fixed-height column; header
        and footer must remain visible at all times so the user always
        sees the mic/pause controls. Wrapping the middle children in a
        flex:1 container with min-height:0 + overflow-y:auto guarantees
        that if the combined transcript + workflow + partial content
        ever exceeds available space, it scrolls INSIDE this region
        instead of pushing the footer off-screen.
      */}
      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isMicMuted ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              margin: "0 16px 8px",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(251, 191, 36, 0.30)",
              background: "rgba(251, 191, 36, 0.10)",
              color: "#FCD34D",
              fontSize: 11,
              lineHeight: 1.4,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">🔇</span>
            <span>
              {muteReason === "encounter_recording_active"
                ? "Muted while the encounter recording is active. Voice resumes when you stop the recording."
                : muteReason === "user_paused"
                ? "Paused. Tap resume to send audio again."
                : muteReason === "idle_silence"
                ? "Paused after a bit of silence — mic muted to save resources. Tap resume (or the mic) to continue."
                : "Voice input is muted."}
            </span>
          </div>
        ) : null}

        <TranscriptPanel turns={transcript} />

        <WorkflowCard
          active={goalStore.active}
          outcome={goalStore.outcome}
          onConfirm={handleGoalConfirm}
          onCancel={handleGoalCancel}
        />

        <LivePartialStrip partial={livePartial} />
      </div>

      <footer
        style={{
          padding: isCompact ? "8px 20px 12px" : "12px 20px 20px",
          borderTop: `1px solid ${tokens.surface.border}`,
          display: "flex",
          flexDirection: "column",
          gap: isCompact ? 8 : 14,
          flexShrink: 0,
        }}
      >
        {budgetNotice ? (
          <div
            role="status"
            style={{
              margin: 0,
              padding: "10px 12px",
              borderRadius: 12,
              border:
                budgetNotice.level === "final"
                  ? "1px solid rgba(96, 165, 250, 0.30)"
                  : "1px solid rgba(251, 191, 36, 0.30)",
              background:
                budgetNotice.level === "final"
                  ? "rgba(59, 130, 246, 0.10)"
                  : "rgba(251, 191, 36, 0.10)",
              color: budgetNotice.level === "final" ? "#BFDBFE" : "#FCD34D",
              fontSize: 12,
              lineHeight: 1.45,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <span>{budgetNotice.message}</span>
            {budgetNotice.resets_at ? (
              <span style={{ opacity: 0.8, fontSize: 11 }}>
                Voice resumes {formatResetTime(budgetNotice.resets_at) || "shortly"}.
              </span>
            ) : null}
          </div>
        ) : null}
        {errorMessage ? (
          <p
            role="alert"
            style={{
              margin: 0,
              padding: "8px 12px",
              borderRadius: 12,
              border: "1px solid rgba(244, 63, 94, 0.25)",
              background: "rgba(244, 63, 94, 0.10)",
              color: "#FECACA",
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            {errorMessage}
          </p>
        ) : null}
        {controlPaneError ? (
          <p
            role="status"
            style={{
              margin: 0,
              padding: "6px 10px",
              borderRadius: 12,
              border: "1px solid rgba(251, 191, 36, 0.25)",
              background: "rgba(251, 191, 36, 0.08)",
              color: "#FCD34D",
              fontSize: 11,
              lineHeight: 1.4,
            }}
          >
            Control plane: {controlPaneError}
          </p>
        ) : null}

        <AudioVisualizer
          active={status === "connected"}
          levelRef={visualizerLevelRef}
          variant={visualizerVariant}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
          }}
        >
          <MicButton
            status={status}
            levelRef={visualizerLevelRef}
            onToggle={handleToggle}
            compact={isCompact}
          />
          {/*
            Pause/resume is a secondary control that only makes sense once
            audio is actually flowing. Before the session starts (idle /
            connecting) and after it stops it is unmounted entirely, so the
            mic sits alone and centered. While connected the mic itself
            renders a Stop icon (see MicButton) and doubles as the stop
            control, with pause alongside it. AnimatePresence collapses the
            slot to zero width on exit so the mic glides back to center
            rather than the pause button popping in/out.
          */}
          <AnimatePresence initial={false}>
            {status === "connected" ? (
              <motion.div
                key="pause"
                initial={{ opacity: 0, scale: 0.6, width: 0 }}
                animate={{ opacity: 1, scale: 1, width: "auto" }}
                exit={{ opacity: 0, scale: 0.6, width: 0 }}
                transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <PauseResumeButton
                  paused={isPaused}
                  disabled={isAnyRecordingActive || isPauseLocked}
                  onToggle={handleTogglePause}
                  compact={isCompact}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        <p
          style={{
            margin: 0,
            textAlign: "center",
            fontSize: 11,
            color: tokens.ink[300],
            letterSpacing: "0.02em",
          }}
        >
          {status === "connected"
            ? "Speak naturally — interrupt anytime."
            : "Hands-free clinical assistant"}
        </p>

        <UsageSection usage={usageSnapshot} />
      </footer>
    </motion.section>
  );
}
